import path from 'path';
import { directoryExists, fileExists, findFiles, readText } from '../utils/fs.js';
import { isGitRepo, getCurrentBranch } from '../utils/git.js';
import { findCsprojFiles, hasEfCore, findDbContextName, getTargetFramework } from '../utils/dotnet.js';
import { logger } from './logger.js';
import type { ProjectInfo } from '../types/index.js';
import { glob } from 'glob';

/**
 * Find web project folder - auto-detects any web project, not hardcoded to "smartstack-web"
 * Searches for: web/{name}/package.json, web/package.json, client/package.json, frontend/package.json
 */
async function findWebProjectFolder(projectPath: string): Promise<string | null> {
  // Priority 1: web/{project-name}-web/ (e.g., web/myproject-web/)
  const webSubfolders = await glob('web/*/', { cwd: projectPath, absolute: true });
  for (const folder of webSubfolders) {
    const packageJsonPath = path.join(folder, 'package.json');
    if (await fileExists(packageJsonPath)) {
      return folder;
    }
  }

  // Priority 2: web/ directly (monorepo style)
  const webDirect = path.join(projectPath, 'web');
  if (await fileExists(path.join(webDirect, 'package.json'))) {
    return webDirect;
  }

  // Priority 3: client/ or frontend/ folders
  const alternativeFolders = ['client', 'frontend', 'ui'];
  for (const folderName of alternativeFolders) {
    const altPath = path.join(projectPath, folderName);
    if (await fileExists(path.join(altPath, 'package.json'))) {
      return altPath;
    }
  }

  // Priority 4: src/web/ for nested structures
  const srcWeb = path.join(projectPath, 'src', 'web');
  if (await fileExists(path.join(srcWeb, 'package.json'))) {
    return srcWeb;
  }

  return null;
}

/**
 * Detect project information for SmartStack
 */
export async function detectProject(projectPath: string): Promise<ProjectInfo> {
  logger.debug('Detecting project info', { path: projectPath });

  const info: ProjectInfo = {
    name: path.basename(projectPath),
    version: '0.0.0',
    isGitRepo: false,
    hasDotNet: false,
    hasEfCore: false,
    hasReact: false,
    csprojFiles: [],
  };

  // Check if directory exists
  if (!(await directoryExists(projectPath))) {
    logger.warn('Project path does not exist', { path: projectPath });
    return info;
  }

  // Git detection
  info.isGitRepo = await isGitRepo(projectPath);
  if (info.isGitRepo) {
    info.currentBranch = await getCurrentBranch(projectPath) || undefined;
  }

  // .NET detection
  info.csprojFiles = await findCsprojFiles(projectPath);
  info.hasDotNet = info.csprojFiles.length > 0;

  // EF Core detection
  if (info.hasDotNet) {
    for (const csproj of info.csprojFiles) {
      if (await hasEfCore(csproj)) {
        info.hasEfCore = true;
        info.dbContextName = await findDbContextName(path.dirname(csproj)) || undefined;
        break;
      }
    }
  }

  // React detection - search for any web project (not hardcoded to smartstack-web)
  const webFolder = await findWebProjectFolder(projectPath);
  if (webFolder) {
    const packageJsonPath = path.join(webFolder, 'package.json');
    if (await fileExists(packageJsonPath)) {
      try {
        const packageJson = JSON.parse(await readText(packageJsonPath));
        info.hasReact = !!packageJson.dependencies?.react;
        info.version = packageJson.version || info.version;
      } catch {
        // Ignore parse errors
      }
    }
  }

  // Try to get version from main csproj (any *.Api.csproj, not hardcoded to SmartStack)
  if (info.csprojFiles.length > 0) {
    const mainCsproj = info.csprojFiles.find(f => /\.Api\.csproj$/i.test(f))
      || info.csprojFiles.find(f => /Api\.csproj$/i.test(f))
      || info.csprojFiles[0];
    const targetFramework = await getTargetFramework(mainCsproj);
    if (targetFramework) {
      logger.debug('Target framework detected', { framework: targetFramework });
    }
  }

  logger.info('Project detected', info);
  return info;
}

/**
 * Find SmartStack project structure
 */
export interface SmartStackStructure {
  root: string;
  domain?: string;
  application?: string;
  infrastructure?: string;
  api?: string;           // Primary API (prefers Api.Core over Api)
  apiCore?: string;       // *.Api.Core project (core controllers)
  apiExtensions?: string; // *.Api project without Core (extension controllers)
  web?: string;
  migrations?: string;
}

export async function findSmartStackStructure(projectPath: string): Promise<SmartStackStructure> {
  const structure: SmartStackStructure = { root: projectPath };

  const csprojFiles = await findCsprojFiles(projectPath);

  for (const csproj of csprojFiles) {
    const projectName = path.basename(csproj, '.csproj').toLowerCase();
    const projectDir = path.dirname(csproj);

    if (projectName.includes('domain')) {
      structure.domain = projectDir;
    } else if (projectName.includes('application')) {
      structure.application = projectDir;
    } else if (projectName.includes('infrastructure')) {
      structure.infrastructure = projectDir;
    } else if (projectName.includes('api.core')) {
      // *.Api.Core project - primary API with core controllers
      structure.apiCore = projectDir;
      // Also set as primary api (Api.Core has priority)
      structure.api = projectDir;
    } else if (projectName.includes('api') && !projectName.includes('api.core')) {
      // *.Api project (not Core) - extensions
      structure.apiExtensions = projectDir;
      // Only set as primary api if apiCore not already set
      if (!structure.api) {
        structure.api = projectDir;
      }
    }
  }

  // Find migrations folder
  if (structure.infrastructure) {
    const migrationsPath = path.join(structure.infrastructure, 'Persistence', 'Migrations');
    if (await directoryExists(migrationsPath)) {
      structure.migrations = migrationsPath;
    }
  }

  // Find web folder (auto-detect, not hardcoded to smartstack-web)
  const webFolder = await findWebProjectFolder(projectPath);
  if (webFolder) {
    structure.web = webFolder;
  }

  return structure;
}

/**
 * Find all entity files in domain layer
 */
export async function findEntityFiles(domainPath: string): Promise<string[]> {
  const entityFiles = await findFiles('**/*.cs', { cwd: domainPath });

  // Filter to likely entity files (classes with Id property)
  const entities: string[] = [];

  for (const file of entityFiles) {
    const content = await readText(file);
    // Simple heuristic: file contains a class with Id property
    if (content.match(/public\s+(?:class|record)\s+\w+/) &&
      content.match(/public\s+(?:int|long|Guid|string)\s+Id\s*\{/)) {
      entities.push(file);
    }
  }

  return entities;
}

/**
 * Find all service interfaces in application layer
 */
export async function findServiceInterfaces(applicationPath: string): Promise<string[]> {
  const files = await findFiles('**/I*Service.cs', { cwd: applicationPath });
  return files;
}

/**
 * Find all controller files in API layer
 */
export async function findControllerFiles(apiPath: string): Promise<string[]> {
  const files = await findFiles('**/*Controller.cs', { cwd: apiPath });
  return files;
}
